import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { PrimaryButton } from '@rbilabs/components-library';
import { format, isToday, isTomorrow, sub } from 'date-fns';
import { useIntl } from 'react-intl';

import { IOperatingHours } from 'generated/graphql-gateway';
import { IRestaurantNode } from 'generated/rbi-graphql';
import { useSelectRestaurant } from 'hooks/store-card';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { useFlag } from 'state/launchdarkly';
import {
  TWeekdayAbbreviationInput,
  convertWeekdayAbbreviationToSanityPattern,
  getWeekDay,
} from 'utils/dateTime';
import { LaunchDarklyFlag } from 'utils/launchdarkly';
import { readableHour } from 'utils/restaurant';
import { serviceModeToSanityHoursMap } from 'utils/sanity';

import { DeliveryUnavailable } from '../delivery-unavailable';

import { DeliveryAvailableMessage, DeliveryAvailableTime } from './styled';
import { WeekdayTranslatedFormat } from './types';

export interface DeliveryClosedAtMomentContainerProps {
  nextEarliestOpen: Date;
  restaurant: IRestaurantNode;
  onClose: VoidFunction;
}

export const DeliveryClosedAtMomentContainer = ({
  nextEarliestOpen,
  restaurant,
  onClose,
}: DeliveryClosedAtMomentContainerProps) => {
  const { formatMessage } = useIntl();
  const hideMobileOrdering = useFlag(LaunchDarklyFlag.HIDE_MOBILE_ORDERING);

  const { availablePickupServiceModes } = useServiceModeStatus(restaurant);

  const { selectRestaurant } = useSelectRestaurant({
    restaurant,
  });

  const nextDeliveryOpenDay = useCallback(() => {
    const formattedWeekday = getWeekDay(nextEarliestOpen) as WeekdayTranslatedFormat;

    if (isTomorrow(nextEarliestOpen)) {
      return formatMessage({ id: 'tomorrow' });
    }
    if (isToday(nextEarliestOpen)) {
      return formatMessage({ id: 'today' });
    } else {
      return formatMessage({ id: `${formattedWeekday}` });
    }
  }, [formatMessage, nextEarliestOpen]);

  const formattedHour = format(nextEarliestOpen, 'HH:mm:ss');

  const isWithinTimeRange = (
    time: string,
    open: string | null | undefined,
    close: string | null | undefined,
    yesterdayClose: string | null | undefined
  ) => {
    if (yesterdayClose && time <= yesterdayClose) {
      return true;
    }
    return open && close && time >= open && time <= close;
  };

  const isPickUpEnabled = useMemo(() => {
    const dateNow = new Date();
    const timeNow = format(dateNow, 'HH:mm:ss');
    const abbreviatedToday = format(dateNow, 'iii').toLowerCase() as TWeekdayAbbreviationInput;
    const abbreviatedYesterday = format(
      sub(dateNow, { days: 1 }),
      'iii'
    ).toLowerCase() as TWeekdayAbbreviationInput;

    return availablePickupServiceModes.some(serviceMode => {
      const serviceModeHours =
        serviceModeToSanityHoursMap[serviceMode as keyof typeof serviceModeToSanityHoursMap];
      if (!restaurant || !serviceModeHours) {
        return false;
      }

      type TOperatingHoursKey = keyof IOperatingHours;
      const todayKeyOpen: TOperatingHoursKey = `${convertWeekdayAbbreviationToSanityPattern(abbreviatedToday)}Open`;
      const todayKeyClose: TOperatingHoursKey = `${convertWeekdayAbbreviationToSanityPattern(abbreviatedToday)}Close`;
      const yesterdayKeyClose: TOperatingHoursKey = `${convertWeekdayAbbreviationToSanityPattern(abbreviatedYesterday)}Close`;

      const restaurantOpenHours = restaurant[serviceModeHours];
      const serviceModeOpen = restaurantOpenHours?.[todayKeyOpen];
      const serviceModeClose = restaurantOpenHours?.[todayKeyClose];
      const serviceModeYesterdayClose = restaurant[serviceModeHours]?.[yesterdayKeyClose];

      return isWithinTimeRange(
        timeNow,
        serviceModeOpen,
        serviceModeClose,
        serviceModeYesterdayClose
      );
    });
  }, [availablePickupServiceModes, restaurant]);

  const handlePickUpButton = useCallback(() => {
    selectRestaurant();
  }, [selectRestaurant]);

  const renderMessage = useMemo(
    () => (
      <>
        {formatMessage({ id: 'deliveryAvailableTimeMessage' })}
        <strong> {nextDeliveryOpenDay().toLowerCase()} </strong>
        {formatMessage({ id: 'at' })}
        <DeliveryAvailableTime>
          {readableHour(
            formattedHour,
            undefined,
            formatMessage({ id: 'restaurantInfoTimeFormat' })
          )}
        </DeliveryAvailableTime>
        {isPickUpEnabled && !hideMobileOrdering && (
          <DeliveryAvailableMessage>
            {formatMessage(
              { id: 'chooseOrderPickup' },
              { orderPickupServiceModes: formatMessage({ id: 'orderPickupMessage' }) }
            )}
          </DeliveryAvailableMessage>
        )}
      </>
    ),
    [formatMessage, formattedHour, isPickUpEnabled, nextDeliveryOpenDay, hideMobileOrdering]
  );

  const renderPrimaryButton = useMemo(
    () =>
      isPickUpEnabled && !hideMobileOrdering ? (
        <PrimaryButton
          fullWidth
          aria-label={formatMessage({ id: 'orderPickupMessage' })}
          onClick={handlePickUpButton}
        >
          {formatMessage({ id: 'orderPickupMessage' })}
        </PrimaryButton>
      ) : undefined,
    [formatMessage, handlePickUpButton, isPickUpEnabled, hideMobileOrdering]
  );

  return (
    <DeliveryUnavailable
      title={formatMessage({ id: 'deliveryClosedNow' })}
      message={renderMessage}
      primaryButton={renderPrimaryButton}
      close={() => onClose()}
    />
  );
};
